import "./sessionHistory.css";
import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getReportClassList } from "hooks/useFilterPersistence/useFilterPersistence";
import { Alert } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import { constants, logSessionHistory } from "utils";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { request } from "hooks/useOkoApi/useOkoApi";
import { SessionSummaries } from "./SessionSummaries/SessionSummaries";
import { normalizeReportTab } from "./SessionSummaries/SessionReportTabs";
import { SessionHistoryFilters } from "./SessionHistoryFilters";
import { LessonReports } from "./LessonReports/LessonReports";
import { LessonReportFilters } from "./LessonReports/LessonReportFilters";
import { hasCompletedSession, useLessonReports } from "./LessonReports/useLessonReports";
import { LessonReportsSkeleton } from "./SessionListSkeleton";
import { useLiveSessions } from "hooks/useLiveSessionsStream/LiveSessionsProvider";
import { PAGE_TABS, initialTab } from "./sessionReportTabs";
import { BetaBanner } from "components/shared/BetaBanner/BetaBanner";
import { useFeatureFlagStatus } from "hooks/useFeatureFlag";
import { logError, LogCategory } from "utils/sentryLogger";
import { saveFilters, loadFilters, loadSharedFilters } from "utils/persistFilters";
import { useLandingPageView, useViewedPageTracking } from "hooks/usePageViewTracker";

const DEFAULT_FILTERS = {
  classroomId: null,
  studentId: null,
  standardCode: null,
  collaborationLevel: null,
  dateRange: "last_4_weeks",
};

const DEFAULT_LESSON_FILTERS = {
  classroomId: null,
  lessonId: null,
  dateRange: "last_4_weeks",
};

// The filters a deep link names in its URL. A transcript link (quote +
// session_id) also pins the list to that one session: it shows no matter what
// filters are saved, over all time unless the link scopes the window itself.
const filtersFromUrl = (params) => {
  const startDate = params.get("start_date") || undefined;
  let dateRange = params.get("date_range") || undefined;
  const isAlertLink = Boolean(params.get("alert") && !params.get("session_id"));
  if (isAlertLink && !dateRange && !startDate) {
    dateRange = "all_time";
  }
  const sessionId =
    ((params.get("quote") || params.get("quote_id") || params.get("report_tab")) && params.get("session_id"))
      ? params.get("session_id")
      : null;
  const filters = {
    ...(params.get("student_id") && { studentId: params.get("student_id") }),
    ...(params.get("classroom_id") && { classroomId: params.get("classroom_id") }),
    ...(startDate && {
      startDate,
      ...(params.get("end_date") && { endDate: params.get("end_date") }),
      dateRange: null,
    }),
    ...(params.get("educator_id") && { educatorId: params.get("educator_id") }),
    ...(params.get("standard_code") && { standardCode: params.get("standard_code") }),
    ...(dateRange && { dateRange }),
    ...(params.get("assignment_id") && { assignmentId: params.get("assignment_id") }),
    ...(sessionId && { sessionId, ...(!startDate && !params.get("date_range") && { dateRange: "all_time" }) }),
  };
  return { filters, hasUrlParams: Object.keys(filters).length > 0 || isAlertLink };
};

const TAB_PAGE_NAME = {
  lessons: "Lesson Reports List",
  groups: "Session History List",
};

export const SessionHistory = ({ educatorId = null, sharedClassId, onClassChange } = {}) => {
  const { authUserData, loading, error: authError } = useAuthUserData();
  const history = useHistory();
  const location = useLocation();
  const isAdminView = Boolean(educatorId);
  // isLoading too: the remote flag reads false until its answer arrives, and
  // the one-time landing view must not go out before then (it would miss the
  // Groups tab the answer can reveal).
  const { enabled: lessonReportEnabled, isLoading: lessonReportFlagLoading } =
    useFeatureFlagStatus("lessonReport");
  // Lesson reports are gated by the lesson_report flag (targeted per user —
  // the admins today): anyone outside it gets the session history alone
  // (Groups sessions and their filters), with no tab bar at all. The admin
  // impersonation view is already behind the admin routes, so it always shows
  // the full page.
  const canShowLessonReports = lessonReportEnabled || isAdminView;
  const [isSessionSummariesLoading, setIsSessionSummariesLoading] = useState(false);
  const [isLastSessionSummariesPage, setIsLastSessionSummariesPage] = useState(false);
  const [error, setError] = useState();
  const [challengeDataErrors, setChallengeDataErrors] = useState({});
  const [challengeData, setChallengeData] = useState({});
  const [isChallengeDataLoading, setIsChallengeDataLoading] = useState({});
  const queryClient = useQueryClient();
  const [educatorAccountData, setEducatorAccountData] = useState(null);
  const [filters, setFilters] = useState(() => {
    const { filters: urlFilters, hasUrlParams } = filtersFromUrl(new URLSearchParams(location.search));
    if (urlFilters.sessionId) {
      return { ...DEFAULT_FILTERS, ...urlFilters, ...(educatorId && { educatorId }) };
    }
    const saved = (!hasUrlParams && !isAdminView) ? loadFilters("session_history") : null;
    const shared = (!hasUrlParams && !isAdminView) ? loadSharedFilters() : null;
    // Widening to All Classes here is a deliberate choice, and this page's own
    // snapshot is the only record of it: `saveFilters` leaves the shared
    // cross-tab class alone when a page saves without one, so the shared class
    // would otherwise walk back in on the next visit and undo the widening.
    // Other pages keep reading that shared class, which is why it is left as
    // it is rather than cleared for everyone.
    const clearedHere = Boolean(saved) && "classroomId" in saved && !saved.classroomId;
    // When shared has a class context, it's authoritative for student/standard
    // (latest cross-tab intent). Absence of those fields in shared = cleared —
    // don't fall back to the page's saved values in that case.
    const hasSharedContext = Boolean(shared?.selectedClassId) && !clearedHere;
    return {
      ...DEFAULT_FILTERS,
      ...(saved && {
        ...(saved.classroomId && { classroomId: saved.classroomId }),
        ...(saved.dateRange && { dateRange: saved.dateRange }),
        ...(saved.collaborationLevel && { collaborationLevel: saved.collaborationLevel }),
        ...(!hasSharedContext && saved.studentId && { studentId: saved.studentId }),
        ...(!hasSharedContext && saved.standardCode && {
          standardCode: saved.standardCode,
          standardDisplayLabel: saved.standardDisplayLabel || null,
        }),
      }),
      ...(hasSharedContext && {
        classroomId: shared.selectedClassId,
        studentId: shared.selectedStudentId || null,
        standardCode: shared.selectedStandard || null,
        standardDisplayLabel: shared.standardDisplayLabel || null,
      }),
      ...urlFilters,
      ...(educatorId && { educatorId }),
      ...(sharedClassId && { classroomId: sharedClassId }),
    };
  });
  const [activeTab, setActiveTab] = useState(() => initialTab(location.search));
  // A teacher who has never finished a session gets no Lessons tab at all —
  // an unfiltered all-time probe decides, so a report outside the current
  // filters still counts. The probe's polling also surfaces the tab once the
  // first report lands, without a reload.
  const lessonProbe = useLessonReports(
    { dateRange: "all_time", ...(educatorId && { educatorId }) },
    canShowLessonReports,
  );
  const showLessonReports =
    canShowLessonReports && lessonProbe.reports.some(hasCompletedSession);
  // Derived, not synced: the admin answer arrives async, so a non-admin's
  // "lessons" default (or deep link) is simply read as Groups rather than
  // waiting on an effect to correct the state. An explicit ?tab=lessons (the
  // side nav's By Lesson link) is honoured even before the first report
  // lands, so the link opens the Lessons view with its empty state instead of
  // silently showing Groups under a highlighted By Lesson.
  const explicitLessons = new URLSearchParams(location.search).get("tab") === "lessons";
  const effectiveTab =
    showLessonReports || (canShowLessonReports && explicitLessons) ? activeTab : "groups";
  // The route's own view waits for the probe, since only then is it known
  // whether a tab bar is shown. A Groups landing with the bar carries its tab;
  // a Lessons landing gets the tab-less view here and its tab view below.
  const landsOnGroupsTab = showLessonReports && effectiveTab === "groups";
  useLandingPageView(
    TAB_PAGE_NAME.groups,
    landsOnGroupsTab ? { tab: PAGE_TABS.find((t) => t.key === "groups").label } : {},
    {
      ready: !isAdminView && !lessonReportFlagLoading && !(canShowLessonReports && lessonProbe.loading),
    }
  );
  // Tracks the visible tab. Lessons is also tracked on mount, since it is the
  // default tab and most teachers never switch; a first render on Groups is
  // skipped because the route page view already covers it.
  const lastTabViewRef = useRef(null);
  const { canTrack, trackView } = useViewedPageTracking();
  useEffect(() => {
    if (!canTrack) return;
    if (!showLessonReports) return;
    if (lastTabViewRef.current === null && effectiveTab === "groups") {
      lastTabViewRef.current = effectiveTab;
      return;
    }
    if (lastTabViewRef.current === effectiveTab) return;
    lastTabViewRef.current = effectiveTab;
    const label = PAGE_TABS.find((tab) => tab.key === effectiveTab)?.label;
    trackView(TAB_PAGE_NAME[effectiveTab], { tab: label });
  }, [canTrack, trackView, showLessonReports, effectiveTab]);
  // The side nav and the page header read the view off ?tab=. A bare URL is
  // decided here (flag, probe, session deep link), so write the answer back
  // once it is known and the three surfaces agree.
  useEffect(() => {
    if (canShowLessonReports && lessonProbe.loading) return;
    const params = new URLSearchParams(location.search);
    if (params.has("tab")) return;
    params.set("tab", effectiveTab);
    history.replace(`${location.pathname}?${params.toString()}`);
  }, [canShowLessonReports, lessonProbe.loading, effectiveTab, location.pathname, location.search, history]);
  const [lessonFilters, setLessonFilters] = useState(() => {
    const saved = !isAdminView ? loadFilters("lesson_reports") : null;
    return {
      ...DEFAULT_LESSON_FILTERS,
      ...(saved && {
        ...(saved.classroomId && { classroomId: saved.classroomId }),
        ...(saved.lessonId && { lessonId: saved.lessonId }),
        ...(saved.dateRange && { dateRange: saved.dateRange }),
      }),
      ...(educatorId && { educatorId }),
      ...(sharedClassId && { classroomId: sharedClassId }),
    };
  });
  const [highlightSessionId, setHighlightSessionId] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("session_id") || null;
  });
  const [highlightReportTab, setHighlightReportTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    return normalizeReportTab(params.get("report_tab"));
  });
  const [highlightStudentName, setHighlightStudentName] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("highlight_student") || null;
  });
  const [highlightQuote, setHighlightQuote] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("quote")
      ? { timestamp: params.get("quote_ts") || null, text: params.get("quote") }
      : null;
  });
  const [alertError, setAlertError] = useState(null);
  const alertInFlightRef = useRef(null);

  // A safety-alert email deep link names the flagged quote by its row id
  // rather than by timestamp + text: SessionSummary resolves it against the
  // session's own tagged quotes once they're fetched.
  const [highlightQuoteId, setHighlightQuoteId] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("quote_id") || null;
  });
  const [reportTab, setReportTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("report_tab") || null;
  });

  // Resolve alert token from URL when alert param is present without session_id
  useEffect(() => {
    if (loading || authUserData?.isAnonymous) return;
    const params = new URLSearchParams(location.search);
    const alertToken = params.get("alert");
    const sessionIdParam = params.get("session_id");

    if (!alertToken || sessionIdParam) {
      alertInFlightRef.current = null;
      return;
    }
    if (alertInFlightRef.current === alertToken) return;
    alertInFlightRef.current = alertToken;

    let cancelled = false;
    const resolveAlert = async () => {
      try {
        const response = await request(`safety-alert-links/${encodeURIComponent(alertToken)}`, {
          method: "GET",
        });
        if (cancelled) return;
        const resolvedSessionId = response?.session_id;
        // Still running (ended_at explicitly null) and not the admin view: its
        // report doesn't exist yet, so open the live pill instead. The tab and
        // quote travel along so the live page can link to the report if the
        // session has ended by then. A response without the field keeps the
        // report flow.
        if (resolvedSessionId && response.ended_at === null && !educatorId) {
          const live = new URLSearchParams({ session_id: resolvedSessionId });
          ["report_tab", "quote_id"].forEach((key) => {
            if (params.get(key)) live.set(key, params.get(key));
          });
          history.replace(`${constants.LIVE_SESSIONS_PATH}?${live}`);
          return;
        }
        if (resolvedSessionId) {
          setAlertError(null);
          setHighlightSessionId(resolvedSessionId);
          setHighlightReportTab(normalizeReportTab(params.get("report_tab")));
          setReportTab(params.get("report_tab") || null);
          setActiveTab("groups");
          setFilters((prev) => ({
            ...prev,
            ...DEFAULT_FILTERS,
            sessionId: resolvedSessionId,
            dateRange: params.get("date_range") || "all_time",
            ...(educatorId && { educatorId }),
          }));
        } else {
          setAlertError("This safety alert link is invalid or has expired.");
        }
      } catch (e) {
        if (cancelled) return;
        logError("Failed to resolve safety alert link", e, {
          category: LogCategory.API,
        });
        setAlertError(
          e?.status === 404 || e?.statusCode === 404
            ? "This safety alert link is invalid or has expired."
            : "Unable to load the safety alert session."
        );
      }
    };

    resolveAlert();

    return () => {
      cancelled = true;
      alertInFlightRef.current = null;
    };
  }, [location.search, loading, authUserData?.isAnonymous, educatorId, history]);

  // Sync filters from URL params when location changes (e.g. history.push from another page).
  // Skipped in admin view — the parent passes educatorId/sharedClassId via props instead.
  useEffect(() => {
    if (isAdminView) return;
    const params = new URLSearchParams(location.search);
    if (params.get("alert") && !params.get("session_id")) {
      setHighlightReportTab(normalizeReportTab(params.get("report_tab")));
      setReportTab(params.get("report_tab") || null);
      setActiveTab("groups");
      return;
    }
    const { filters: urlFilters, hasUrlParams } = filtersFromUrl(params);
    if (hasUrlParams) {
      setFilters({ ...DEFAULT_FILTERS, ...urlFilters });
    } else {
      // The side nav's By Session and By Lesson rows rewrite the URL to a
      // bare ?tab=, so a deep link's session or bundle pin has left the URL;
      // release it from the filters too, or the list stays on that one
      // session with nothing on screen to explain why.
      setFilters((prev) =>
        prev.sessionId || prev.assignmentId ? { ...prev, sessionId: null, assignmentId: null } : prev,
      );
    }
    setHighlightSessionId(params.get("session_id") || null);
    setHighlightStudentName(params.get("highlight_student") || null);
    setHighlightQuote(
      params.get("quote")
        ? { timestamp: params.get("quote_ts") || null, text: params.get("quote") }
        : null,
    );
    setHighlightQuoteId(params.get("quote_id") || null);
    setHighlightReportTab(normalizeReportTab(params.get("report_tab")));
    setReportTab(params.get("report_tab") || null);
    setActiveTab(initialTab(location.search));
  }, [location.search, isAdminView]);
  const [schoolSetupWarning, setSchoolSetupWarning] = useState(null);

  useEffect(() => {
    const fetchEducatorData = async () => {
      try {
        // Only `.classes` is read from this, so the cached report class list
        // serves both the impersonation and signed-in variants.
        const classList = await getReportClassList(queryClient, educatorId);
        setEducatorAccountData({ classes: classList || [] });
      } catch (e) {
        const errorMessage = e?.detail || e?.message || "An unknown error occurred.";
        // If school isn't set up, show warning but still display sessions
        if (errorMessage.includes("school hasn't been set up")) {
          setSchoolSetupWarning(errorMessage);
        } else {
          logError("Failed to fetch educator account data", e, {
            category: LogCategory.ERROR,
          });
          setError(errorMessage);
        }
      }
    };
    fetchEducatorData();
  }, [educatorId, queryClient]);

  // Provided by the educator chrome (SidebarView); the admin tool's
  // impersonated view mounts without one and reads a disabled stream.
  const liveStream = useLiveSessions();
  // Ending a session from this page: drop it from the live state now and re-read
  // the top of the list so the row surfaces as "Analyzing" without waiting on a push.
  const [refreshNonce, setRefreshNonce] = useState(0);
  const handleSessionEnded = (sessionId) => {
    liveStream.endSession(sessionId);
    setRefreshNonce((n) => n + 1);
  };

  if (loading) return <></>;
  if (error || authError) return <div style={{
    textAlign: "center",
    textOverflow: "ellipsis",
    overflow: "auto"
  }}>
    <Alert dismissible
      variant="danger"
      style={{ overflowWrap: "break-word", width: "auto", display: "inline-block", marginBottom: 0 }}
      onClose={() => setError(null)}>
      {error || authError.message}
    </Alert>
  </div>;

  const hasClasses = educatorAccountData?.classes && educatorAccountData.classes.length > 0;

  const handleLessonFilterChange = (newFilters) => {
    setLessonFilters(newFilters);
    if (!isAdminView) {
      saveFilters("lesson_reports", {
        classroomId: newFilters.classroomId,
        lessonId: newFilters.lessonId,
        dateRange: newFilters.dateRange,
      });
    }
    if (onClassChange && newFilters.classroomId !== lessonFilters.classroomId) {
      onClassChange(newFilters.classroomId);
    }
  };

  const handleFilterChange = (newFilters) => {
    const classChanged = newFilters.classroomId !== filters.classroomId;
    if (classChanged) {
      const selectedClass = educatorAccountData?.classes?.find(c => c.id === newFilters.classroomId);
      logSessionHistory("Session history: Classroom filter applied", {
        event: "filter_applied",
        classroomId: newFilters.classroomId,
        classroomName: newFilters.classroomId === null ? "All Classes" : selectedClass?.name,
      });
      if (onClassChange) onClassChange(newFilters.classroomId);
    }
    setFilters(newFilters);
    // Releasing a deep link's session or bundle pin must also leave the URL,
    // or a refresh re-pins. The pinned URL carries the link's own filters too
    // (student, standard, date window), which the location-sync effect would
    // reapply over the change just made, so only the tab survives; dropping
    // the quote params lets that effect clear the highlight state as well.
    const searchParams = new URLSearchParams(location.search);
    const pinReleased =
      (filters.sessionId && !newFilters.sessionId) ||
      (filters.assignmentId && !newFilters.assignmentId) ||
      (searchParams.has("alert") && !newFilters.sessionId);
    if (pinReleased) history.replace(`${location.pathname}?tab=groups`);
    // startDate/endDate only ever come from a report deep link; remembering
    // them pinned every later plain visit to that week, hiding new sessions
    // until Clear All.
    if (!isAdminView) {
      saveFilters("session_history", {
        classroomId: newFilters.classroomId,
        studentId: newFilters.studentId,
        dateRange: newFilters.dateRange,
        collaborationLevel: newFilters.collaborationLevel,
        standardCode: newFilters.standardCode,
        standardDisplayLabel: newFilters.standardDisplayLabel,
      });
    }
  };

  return (
    <div className="session-history">
      {alertError && (
        <Alert
          variant="warning"
          dismissible
          onClose={() => setAlertError(null)}
          style={{ marginBottom: "1rem" }}
        >
          {alertError}
        </Alert>
      )}
      {schoolSetupWarning && (
        <Alert
          variant="warning"
          dismissible
          onClose={() => setSchoolSetupWarning(null)}
          style={{ marginBottom: "1rem" }}
        >
          {schoolSetupWarning}
        </Alert>
      )}
      <BetaBanner
        title="Reports Updated"
        text={
          <>
            We've improved how this report is calculated, so some data may look
            different than before. If you'd like access to the previous version,
            please contact{" "}
            <a href="mailto:support@okolabs.ai">support@okolabs.ai</a>
          </>
        }
      />
      {canShowLessonReports && lessonProbe.loading && activeTab === "lessons" ? (
        // Probe unresolved: don't flash Groups (or a wrong empty state) at a
        // teacher who is about to land on Lessons.
        <LessonReportsSkeleton />
      ) : effectiveTab === "lessons" ? (
        <>
          {hasClasses && (
            <div style={{ marginBottom: "2.5rem" }}>
              <LessonReportFilters
                classes={educatorAccountData.classes}
                filters={lessonFilters}
                onFilterChange={handleLessonFilterChange}
              />
            </div>
          )}
          <LessonReports filters={lessonFilters} />
        </>
      ) : (
        <>
          {hasClasses && (
            <div style={{ marginBottom: "2.5rem" }}>
              <SessionHistoryFilters
                classes={educatorAccountData.classes}
                filters={filters}
                onFilterChange={handleFilterChange}
              />
            </div>
          )}
          <SessionSummaries
            challengeDataErrors={challengeDataErrors}
            setChallengeDataErrors={setChallengeDataErrors}
            challengeData={challengeData}
            setChallengeData={setChallengeData}
            isChallengeDataLoading={isChallengeDataLoading}
            setIsChallengeDataLoading={setIsChallengeDataLoading}
            setIsSessionSummariesLoading={setIsSessionSummariesLoading}
            isSessionSummariesLoading={isSessionSummariesLoading}
            setIsLastSessionSummariesPage={setIsLastSessionSummariesPage}
            isLastSessionSummariesPage={isLastSessionSummariesPage}
            setError={setError}
            filters={filters}
            classes={educatorAccountData?.classes || []}
            highlightSessionId={highlightSessionId}
            highlightStudentName={highlightStudentName}
            highlightQuote={highlightQuote}
            highlightQuoteId={highlightQuoteId}
            highlightReportTab={highlightReportTab}
            reportTab={reportTab}
            statusEvents={liveStream.statusEvents}
            snapshotCount={liveStream.snapshotCount}
            refreshNonce={refreshNonce}
            onSessionEnded={handleSessionEnded}
          />
        </>
      )}
    </div>
  )
};

